import React, { useState, useRef, useCallback } from 'react';
import { createWorker } from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';
import { useNavigate } from 'react-router-dom';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];

export default function AdminOCRPanel() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pageProgress, setPageProgress] = useState('');
  const [extractedText, setExtractedText] = useState('');
  const [error, setError] = useState('');
  const [copyMsg, setCopyMsg] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const reset = () => {
    setFile(null);
    setPreview(null);
    setExtractedText('');
    setProgress(0);
    setPageProgress('');
    setError('');
    setCopyMsg('');
  };

  const handleFile = (f) => {
    setError('');
    setExtractedText('');
    setProgress(0);
    setPageProgress('');
    setCopyMsg('');
    setFile(f);
    if (f.type.startsWith('image/')) {
      const r = new FileReader();
      r.onload = (e) => setPreview(e.target.result);
      r.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && ALLOWED_TYPES.includes(f.type)) handleFile(f);
    else if (f) setError('Formato no soportado. Usa PNG, JPG, WEBP o PDF.');
  }, []);

  const processOCR = async () => {
    if (!file) return;
    setIsProcessing(true);
    setError('');
    let text = '';

    try {
      if (file.type === 'application/pdf') {
        const buf = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        const total = pdf.numPages;
        const worker = await createWorker('spa');

        for (let i = 1; i <= total; i++) {
          setPageProgress(`Página ${i} de ${total}`);
          setProgress(Math.round((i - 1) / total * 80));

          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 2 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          await page.render({ canvasContext: ctx, viewport }).promise;

          const { data } = await worker.recognize(canvas);
          text += data.text + '\n\n---\n\n';
        }
        await worker.terminate();
        setProgress(90);
      } else {
        setPageProgress('Procesando imagen...');
        const worker = await createWorker('spa');
        worker.setProgressHandler((p) => setProgress(Math.round(p * 80)));
        const { data } = await worker.recognize(file);
        text = data.text;
        await worker.terminate();
        setProgress(85);
      }

      const cleaned = text.replace(/\n{3,}/g, '\n\n').trim();
      setExtractedText(cleaned);
      setProgress(100);
      setPageProgress('Completado');
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(extractedText).then(() => {
      setCopyMsg('¡Texto copiado!');
      setTimeout(() => setCopyMsg(''), 2000);
    });
  };

  const handleGenerateQuiz = () => {
    const encoded = encodeURIComponent(extractedText.slice(0, 50000));
    navigate(`/dashboard/quiz-generator?text=${encoded}`);
  };

  // Herramientas de Limpieza y Formato
  const cleanLineBreaks = () => {
    const cleaned = extractedText
      .replace(/([a-záéíóúñáéíóú,])\n([a-záéíóúñáéíóú])/gi, '$1 $2')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    setExtractedText(cleaned);
  };

  const cleanUppercase = () => {
    setExtractedText(extractedText.toUpperCase());
  };

  const downloadTxt = () => {
    const blob = new Blob([extractedText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ocr_extraido_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
  };

  // Convertidor inteligente de Texto OCR a Cuestionario JSON
  const convertTextToJsonQuiz = () => {
    if (!extractedText.trim()) return;

    // Patrón para dividir por reactivos (ej. 1.-, 1), 2.-, Pregunta 1:)
    const blocks = extractedText.split(/(?=\b(?:\d{1,3}[.)]|Pregunta\s+\d+[:.]))/i).filter(b => b.trim().length > 10);

    if (blocks.length === 0) {
      alert('No se detectó un patrón claro de preguntas (ejemplo: "1.- ¿Pregunta..."). Te recomendamos revisar el texto.');
      return;
    }

    const quizArray = blocks.map((block, idx) => {
      const lines = block.split(/\n/).map(l => l.trim()).filter(Boolean);
      const preguntaLine = lines[0] || `Pregunta ${idx + 1}`;
      
      // Extraer opciones (a), b), c), d) o A., B., C., D.)
      const optionLines = lines.slice(1).filter(l => /^[a-eA-E][.)-]\s+/.test(l));
      
      let respuestas = optionLines.map((optStr, oIdx) => {
        const cleanText = optStr.replace(/^[a-eA-E][.)-]\s+/, '').trim();
        return {
          id: (idx + 1) * 10 + oIdx,
          respuesta: cleanText,
          correcta: oIdx === 0 // Por defecto la primera
        };
      });

      if (respuestas.length === 0) {
        respuestas = [
          { id: (idx + 1) * 10 + 0, respuesta: 'Opción A', correcta: true },
          { id: (idx + 1) * 10 + 1, respuesta: 'Opción B', correcta: false },
          { id: (idx + 1) * 10 + 2, respuesta: 'Opción C', correcta: false },
          { id: (idx + 1) * 10 + 3, respuesta: 'Opción D', correcta: false }
        ];
      }

      return {
        id: idx + 1,
        pregunta: preguntaLine.replace(/^\d+[.)]\s*/, '').trim(),
        respuestas,
        explicacion: 'Extraído automáticamente mediante OCR.'
      };
    });

    const jsonStr = JSON.stringify(quizArray, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cuestionario_ocr_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    alert(`🎉 ¡Se generó exitosamente un archivo Cuestionario JSON con ${quizArray.length} preguntas!`);
  };

  const inputId = 'ocr-file-input';

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: '20px',
      padding: '4px', color: '#e2e8f0', fontFamily: "'Inter', sans-serif",
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #0f1923, #0a1628)',
        border: '1px solid #2a3a5c', borderRadius: '12px', padding: '24px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontFamily: "'Orbitron', sans-serif", fontSize: '1.1rem', color: '#c5a059', letterSpacing: '1px' }}>
            📄 Escáner OCR
          </h3>
          {file && (
            <button onClick={reset}  style={{  fontFamily: "'Orbitron', sans-serif" }} className="adm-btn adm-btn--secondary">
              ✕ Nuevo documento
            </button>
          )}
        </div>

        {!file ? (
          <div
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => inputRef.current?.click()}
            style={{
              border: '2px dashed #2a3a5c', borderRadius: '12px',
              padding: '48px 20px', textAlign: 'center', cursor: 'pointer',
              transition: 'all 0.3s', background: 'rgba(255,255,255,0.02)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#c5a059'; e.currentTarget.style.background = 'rgba(197,160,89,0.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2a3a5c'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📤</div>
            <p style={{ margin: '0 0 8px', fontSize: '1rem', color: '#94a3b8' }}>
              Arrastra un archivo aquí o <strong style={{ color: '#c5a059' }}>selecciona</strong>
            </p>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>
              PNG · JPG · WEBP · PDF
            </p>
            <input
              ref={inputRef}
              id={inputId}
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.pdf"
               style={{  display: 'none'  }} className="adm-input"
              onChange={(e) => { const f = e.target.files[0]; if (f) handleFile(f); }}
            />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '16px',
              background: 'rgba(255,255,255,0.03)', borderRadius: '8px',
              padding: '12px 16px', border: '1px solid rgba(255,255,255,0.06)',
            }}>
              {preview ? (
                <img src={preview} alt="preview" style={{
                  width: '64px', height: '64px', objectFit: 'cover',
                  borderRadius: '8px', border: '1px solid #2a3a5c',
                }} />
              ) : (
                <div style={{
                  width: '64px', height: '64px', borderRadius: '8px',
                  background: '#1e293b', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '1.5rem', color: '#c5a059',
                }}>📄</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {file.name}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {(file.size / 1024).toFixed(1)} KB
                </div>
              </div>
              {!isProcessing && !extractedText && (
                <button onClick={processOCR}  style={{  fontFamily: "'Orbitron', sans-serif",  whiteSpace: 'nowrap' }} className="adm-btn adm-btn--primary">
                  INICIAR OCR
                </button>
              )}
            </div>

            {isProcessing && (
              <div style={{
                background: 'rgba(197,160,89,0.08)', borderRadius: '8px',
                padding: '16px', border: '1px solid rgba(197,160,89,0.2)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '8px' }}>
                  <span>{pageProgress || 'Inicializando...'}</span>
                  <span style={{ color: '#c5a059', fontWeight: 700 }}>{progress}%</span>
                </div>
                <div style={{ width: '100%', height: '6px', background: '#1e293b', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${progress}%`, height: '100%',
                    background: 'linear-gradient(90deg, #c5a059, #a8853f)',
                    borderRadius: '10px', transition: 'width 0.3s ease-out',
                  }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: '8px', fontSize: '0.85rem',
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          color: '#ef4444',
        }}>
          {error}
        </div>
      )}

      {extractedText && (
        <div style={{
          background: 'linear-gradient(135deg, #0f1923, #0a1628)',
          border: '1px solid #2a3a5c', borderRadius: '12px', padding: '20px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
            <h4 style={{ margin: 0, fontFamily: "'Orbitron', sans-serif", fontSize: '0.9rem', color: '#c5a059', letterSpacing: '1px' }}>
              📝 Texto extraído ({extractedText.length} caracteres)
            </h4>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={cleanLineBreaks} title="Unir párrafos cortados"  className="adm-btn adm-btn--secondary">
                🧹 Unir Saltos
              </button>
              <button onClick={cleanUppercase} title="Convertir a mayúsculas"  className="adm-btn adm-btn--secondary">
                🔠 Mayúsculas
              </button>
              <button onClick={downloadTxt} title="Descargar como .TXT"  className="adm-btn adm-btn--secondary">
                💾 .TXT
              </button>
              <button onClick={convertTextToJsonQuiz} title="Convertir preguntas detectadas a JSON"  className="adm-btn adm-btn--secondary">
                📦 Convertir a Cuestionario JSON
              </button>
              <button onClick={handleCopy}  style={{  fontFamily: "'Orbitron', sans-serif" }} className="adm-btn adm-btn--secondary">
                {copyMsg || '📋 COPIAR'}
              </button>
              <button onClick={handleGenerateQuiz}  style={{  fontFamily: "'Orbitron', sans-serif" }} className="adm-btn adm-btn--primary">
                ⚡ GENERAR CUESTIONARIO AI
              </button>
            </div>
          </div>
          <textarea
            value={extractedText}
            onChange={(e) => setExtractedText(e.target.value)}
            style={{
              width: '100%', minHeight: '300px', padding: '14px',
              background: '#0a1420', border: '1px solid #1e293b',
              borderRadius: '8px', color: '#e2e8f0', fontSize: '0.85rem',
              lineHeight: 1.6, resize: 'vertical', outline: 'none',
              fontFamily: "'Inter', sans-serif", boxSizing: 'border-box',
            }}
          />
        </div>
      )}

      <div style={{
        background: 'rgba(255,255,255,0.02)', border: '1px solid #1e293b',
        borderRadius: '8px', padding: '12px 16px', fontSize: '0.75rem', color: '#64748b',
      }}>
        ⚓ El OCR se procesa completamente en tu navegador. Los archivos no se suben a ningún servidor.
        Para documentos PDF escaneados, cada página se renderiza y procesa individualmente.
      </div>
    </div>
  );
}
